import React from 'react';
import { Link } from 'react-router-dom';
import { withLiveCycleProgress } from '../../../utils/jobProgressUtils';
import {
  clockTime,
  cycleLabel,
  elapsedSeconds,
  formatDuration,
  formatLeft,
  initials,
  isRerunJob,
  jobTypeLabel,
  parseIso,
  stagesForJob,
} from './pipelineFormat';

function StatusPill({ status }) {
  const s = String(status || '').toLowerCase();
  const tone = s === 'paused'
    ? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
    : s === 'saving' || s === 'finalizing'
      ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200'
      : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200';
  const label = s === 'paused' ? 'Paused' : s === 'saving' ? 'Saving' : s === 'finalizing' ? 'Finishing' : 'Running';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold ${tone}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s === 'paused' ? 'bg-slate-400' : 'bg-indigo-500 shadow-[0_0_0_3px_rgba(79,70,229,.2)]'}`} />
      {label}
    </span>
  );
}

function displayStatus(job) {
  const status = String(job?.status || '').toLowerCase();
  const phase = String(job?.phase || '').toLowerCase();
  if (status === 'paused') return 'paused';
  if (status === 'saving' || phase === 'saving') return 'saving';
  if (phase === 'finalize') return 'finalizing';
  return 'running';
}

/** Segmented whole-job bar: one segment per IEP cycle, sized by its (expected) length. */
function CycleBar({ cycles }) {
  const weight = (c) => c.seconds && c.status === 'done' ? c.seconds : (c.estimated_seconds || (c.seconds || 0) + (c.remaining_seconds || 0) || 600);
  return (
    <div>
      <div className="flex h-2.5 gap-1" role="img" aria-label={`IEP cycles: ${cycles.filter((c) => c.status === 'done').length} of ${cycles.length} done`}>
        {cycles.map((c) => {
          const pct = c.status === 'done' ? 100 : c.status === 'running' && c.sections_total
            ? Math.round((100 * (c.sections_done || 0)) / c.sections_total) : 0;
          return (
            <div key={c.index || c.anchor} className="overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700" style={{ flex: weight(c) }}>
              <div className={`h-full rounded-full ${c.status === 'done' ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex gap-1">
        {cycles.map((c) => (
          <div key={c.index || c.anchor} className="truncate text-[11px] text-gray-500 dark:text-slate-400" style={{ flex: weight(c) }}>
            Cycle {c.index} · {c.status === 'done' ? 'done' : c.status === 'running' ? `${c.sections_done || 0}/${c.sections_total || '…'}` : 'queued'}
          </div>
        ))}
      </div>
    </div>
  );
}

function CycleRow({ c, job }) {
  const done = c.status === 'done';
  const running = c.status === 'running';
  const pct = done ? 100 : running && c.sections_total ? Math.round((100 * (c.sections_done || 0)) / c.sections_total) : 0;
  return (
    <li className={`grid grid-cols-[32px_1fr] items-center gap-x-3 gap-y-2 rounded-xl px-2.5 py-2.5 sm:grid-cols-[32px_minmax(160px,1.2fr)_minmax(200px,2.4fr)_minmax(120px,1fr)] ${running ? 'bg-indigo-50/70 dark:bg-indigo-950/30' : ''}`}>
      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
        done ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
          : running ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}
      >
        {done ? '✓' : c.index}
      </span>
      <div className="min-w-0">
        <p className="text-[13.5px] font-semibold text-gray-900 dark:text-white">Cycle {c.index} · {cycleLabel(c.anchor)}</p>
        <p className="text-[11.5px] text-gray-500 dark:text-slate-400">
          {done ? 'Saved to the report' : running ? 'Running now' : 'Queued'}
          {c.index === 1 ? ' · current IEP' : ''}
        </p>
      </div>
      <div className="col-span-2 sm:col-span-1">
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
            <div className={`h-full rounded-full ${done ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="min-w-[96px] text-right text-[11.5px] text-gray-500 dark:text-slate-400">
            {done ? 'all sections' : running ? `${c.sections_done || 0} of ${c.sections_total || '…'} sections` : 'Waiting'}
          </span>
        </div>
        {running && (job.section_label || job.message) ? (
          <p className="mt-1 truncate text-[11.5px] text-gray-500 dark:text-slate-400">Now: {job.section_label || job.message}</p>
        ) : null}
      </div>
      <div className="col-span-2 text-right sm:col-span-1">
        <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
          {done ? formatDuration(c.seconds) : running ? (formatLeft(c.remaining_seconds) ? `≈ ${formatLeft(c.remaining_seconds)} left` : 'finishing') : `≈ ${formatLeft(c.estimated_seconds) || '—'}`}
        </p>
        <p className="text-[11px] text-gray-500 dark:text-slate-400">
          {done ? 'took' : running ? `${formatDuration(c.seconds)} so far` : 'estimate'}
        </p>
      </div>
    </li>
  );
}

function StageStrip({ job }) {
  const stages = stagesForJob(job);
  const pagesNow = String(job.phase || '').toLowerCase() === 'ocr' && Number(job.total) > 0
    ? `${job.progress || 0} of ${job.total} pages` : null;
  return (
    <ol className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
      {stages.map((s) => {
        const tone = s.state === 'done'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300'
          : s.state === 'running'
            ? 'border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200'
            : 'border-gray-200 bg-white text-gray-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300';
        let main = s.state === 'done' ? 'Done' : s.state === 'running' ? 'In progress' : 'Waiting';
        let sub = '';
        if (s.state === 'running') {
          const left = formatLeft(job.stage_remaining_seconds);
          if (s.key !== 'compliance' && left) main = `≈ ${left} left`;
          sub = s.key === 'ocr' && pagesNow ? `this stage · ${pagesNow}` : 'this stage';
        } else if (s.key === 'compliance' && s.state === 'waiting') {
          sub = 'about 13 min per IEP cycle';
        }
        return (
          <li key={s.key} className={`rounded-xl border px-3 py-2.5 ${tone}`}>
            <p className="flex items-center gap-1.5 text-[12.5px] font-semibold">
              <span aria-hidden="true">{s.state === 'done' ? '✓' : s.state === 'running' ? '●' : '○'}</span>
              {s.label}
            </p>
            <p className="mt-1 text-[13px] font-semibold text-gray-900 dark:text-white">{main}</p>
            {sub ? <p className="text-[11px] text-gray-500 dark:text-slate-400">{sub}</p> : null}
          </li>
        );
      })}
    </ol>
  );
}

const btn = 'inline-flex min-h-[36px] items-center gap-1.5 rounded-xl border px-3 py-1.5 text-[13px] font-medium transition disabled:opacity-50';

export default function RunningJobCard({ job, now, busy, onAction }) {
  const status = displayStatus(job);
  const left = Number(job.remaining_seconds) || 0;
  const elapsed = elapsedSeconds(job.started_at, now);
  const started = parseIso(job.started_at);
  const finishAt = left > 0 ? new Date(now + left * 1000) : null;
  const cycles = withLiveCycleProgress(job.cycles, job);
  const pages = Number(job.total_pages) || 0;
  const name = job.student_name || (job.student_id ? `Student #${job.student_id}` : 'Unknown student');
  const leftText = formatLeft(left);

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800/80" aria-label={`${name} — ${jobTypeLabel(job)}`}>
      <header className="flex flex-wrap items-start gap-3.5 border-b border-gray-100 px-4 py-4 dark:border-slate-700 sm:px-5">
        <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[12px] font-bold text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
          {initials(name)}
        </span>
        <div className="min-w-[200px] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{name}</h3>
            <StatusPill status={status} />
          </div>
          <p className="mt-0.5 text-[12px] text-gray-500 dark:text-slate-400">
            {jobTypeLabel(job)}
            {pages ? ` · ${pages} pages` : ''}
            {cycles.length ? <> · <b className="font-semibold text-gray-800 dark:text-slate-200">{cycles.length} IEP cycles</b></> : ''}
            {started ? ` · started ${clockTime(started)}` : ''}
            {job.started_by_name ? ` by ${job.started_by_name}` : ''}
          </p>
        </div>
        <div className="min-w-[180px] text-right">
          <p className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            {status === 'paused' ? 'Paused' : leftText ? `About ${leftText} left` : 'Finishing up'}
          </p>
          <p className="text-[12px] text-gray-500 dark:text-slate-400">
            {finishAt && status !== 'paused' ? `Finishes around ${clockTime(finishAt)} · ` : ''}
            {elapsed != null ? `${formatDuration(elapsed)} elapsed` : ''}
            {elapsed != null && left > 0 ? ` of ≈ ${formatLeft(elapsed + left)}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {job.can_pause ? (
            <button type="button" disabled={busy} onClick={() => onAction('pause', job)} className={`${btn} border-amber-300 text-amber-800 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30`}>Pause</button>
          ) : null}
          {job.can_start ? (
            <button type="button" disabled={busy} onClick={() => onAction('start', job)} className={`${btn} border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700`}>Start</button>
          ) : null}
          {job.can_stop ? (
            <button type="button" disabled={busy} onClick={() => onAction('stop', job)} className={`${btn} border-red-200 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/30`}>Stop</button>
          ) : null}
          {job.student_id ? (
            <Link to={`/students/${job.student_id}/information`} className={`${btn} border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700`}>Open report</Link>
          ) : null}
        </div>
      </header>

      <div className="space-y-4 px-4 py-4 sm:px-5">
        {!isRerunJob(job) ? <StageStrip job={job} /> : null}
        {cycles.length ? (
          <>
            <CycleBar cycles={cycles} />
            <ol className="-mx-2.5 space-y-0.5">
              {cycles.map((c) => <CycleRow key={c.index || c.anchor} c={c} job={job} />)}
            </ol>
          </>
        ) : isRerunJob(job) ? (
          <div>
            <div className="h-2.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.max(2, Math.min(99, Number(job.overall_pct) || 0))}%` }} />
            </div>
            <p className="mt-1.5 text-[12px] text-gray-500 dark:text-slate-400">
              {job.progress && job.total ? `${job.progress} of ${job.total} sections · ` : ''}{job.section_label ? `Now: ${job.section_label}` : job.message || ''}
            </p>
          </div>
        ) : null}
        {job.error ? <p className="text-[12px] text-red-600 dark:text-red-300">{job.error}</p> : null}
      </div>
    </section>
  );
}
